import classnames from 'classnames';
import Head from 'next/head';
import {
  useCallback,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';

import { Gtm } from '@/core/tracking/utils/Gtm';
import { LISTING_LAYOUT_ID } from '@/productDiscovery/Listings/constants';

import styles from './ListingLayout.module.scss';

interface Props {
  className?: string;
  bodyClassName?: string;
  pageType?: string;
  pageTitle: string;
  children: (
    styles: Record<string, string>,
    properties: [isFiltersOpen: boolean, toggleFiltersPanel: () => void],
  ) => ReactNode;
  newLayout: boolean;
}

export const ListingLayout: FunctionComponent<Props> = ({
  className,
  bodyClassName,
  pageType,
  pageTitle,
  children,
  newLayout,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const rootClassName = classnames(styles.root, className, {
    [styles.filtersOpen]: filtersOpen,
    [styles.newLayout]: newLayout,
  });

  const listingLayoutClassnames = classnames(
    styles.listingLayoutBody,
    bodyClassName,
  );

  const sendDisplayFiltersPanelTracking = useCallback(() => {
    Gtm.push({
      event: 'interaction_display filter panel',
      e_category: 'navigation',
      e_action: 'Display filter panel',
      e_label: pageType,
      amp_event_name: 'Display filter panel',
      event_trigger: 'mobile web',
      interaction_hit: 'True',
    });
  }, [pageType]);

  const toggleFiltersPanel = useCallback(() => {
    if (!filtersOpen) {
      setTimeout(sendDisplayFiltersPanelTracking, 1000);
    }
    setFiltersOpen(!filtersOpen);
  }, [filtersOpen, sendDisplayFiltersPanelTracking]);

  return (
    <>
      <Head>
        <title key="meta_title">{pageTitle}</title>
      </Head>

      <div className={rootClassName} id={LISTING_LAYOUT_ID}>
        <div className={listingLayoutClassnames}>
          {children(styles, [
            filtersOpen,
            () => requestAnimationFrame(toggleFiltersPanel),
          ])}
        </div>
      </div>
    </>
  );
};

ListingLayout.displayName = 'ListingLayout';
